import { useEffect, useState } from "react";
import api from "@/lib/api";

const ICT_TOGGLES = [
  { key: "fvg",       label: "FVG (Fair Value Gaps)" },
  { key: "ifvg",      label: "IFVG (Inverted FVG)" },
  { key: "sweeps",    label: "Liquidity sweeps (BSL / SSL)" },
  { key: "structure", label: "Structure (BOS / MSS arrows)" },
  { key: "smt",       label: "SMT divergence markers" },
  { key: "swings",    label: "Swing highs / lows" },
  { key: "pd",        label: "Premium / Discount zones" },
  { key: "sessions",  label: "Session boxes (Asia / London / NY)" },
];

const SOUND_TOGGLES = [
  { key: "ORDER_FILL", label: "Order fill / Execution" },
  { key: "FVG",       label: "FVG" },
  { key: "IFVG",      label: "IFVG" },
  { key: "BOS",       label: "BOS" },
  { key: "MSS",       label: "MSS" },
  { key: "SWEEP_BSL", label: "Sweep BSL" },
  { key: "SWEEP_SSL", label: "Sweep SSL" },
  { key: "SMT",       label: "SMT" },
  { key: "SESSION",   label: "Session open" },
  { key: "PREMIUM",   label: "Premium" },
  { key: "DISCOUNT",  label: "Discount" },
];

const TIMEFRAMES = ["1m", "5m", "15m", "30m", "1h", "4h", "1d"];

/**
 * SettingsTab — extracted from AccountPage to reduce monolithic size.
 * Manages chart-overlay toggles, per-event audio toggles, AI auto-review,
 * and the per-timeframe × per-concept audio mute matrix.
 *
 * All state is persisted to localStorage (same keys the Dashboard reads).
 */
export default function SettingsTab() {
  const [visualToggles, setVisualToggles] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ict.visual")) || {
      fvg: true, ifvg: true, swings: true, structure: true, sweeps: true, smt: true, pd: true, sessions: true,
    }; } catch { return { fvg:true, ifvg:true, swings:true, structure:true, sweeps:true, smt:true, pd:true, sessions:true }; }
  });
  const [soundToggles, setSoundToggles] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ict.sounds")) || {}; } catch { return {}; }
  });
  const [soundTfFilter, setSoundTfFilter] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ict.sounds.tf")) || {}; } catch { return {}; }
  });
  const [autoReview, setAutoReview] = useState(() => {
    try { return localStorage.getItem("ict.autoReview") !== "0"; } catch { return true; }
  });

  // V2.6.8 — Custom execution sound (only visible when SKU owned)
  const [customSoundOwned, setCustomSoundOwned] = useState(false);
  const [customSoundUrl, setCustomSoundUrl] = useState(() => localStorage.getItem("ict.customExecSound") || "");
  const [customSoundEnabled, setCustomSoundEnabled] = useState(() => localStorage.getItem("ict.customExecSound.enabled") !== "0");
  const [customSoundMsg, setCustomSoundMsg] = useState("");
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/store/me"); setCustomSoundOwned((data.owned || []).includes("custom_execution")); }
      catch {}
    })();
  }, []);
  useEffect(() => { try { localStorage.setItem("ict.customExecSound.enabled", customSoundEnabled ? "1" : "0"); } catch {} }, [customSoundEnabled]);

  function onCustomSoundFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 800_000) { setCustomSoundMsg("Max 800 KB — keep it short!"); setTimeout(() => setCustomSoundMsg(""), 3000); return; }
    const r = new FileReader();
    r.onload = () => {
      const url = r.result;
      try { localStorage.setItem("ict.customExecSound", url); } catch { setCustomSoundMsg("Couldn't save — storage full?"); return; }
      setCustomSoundUrl(url);
      setCustomSoundMsg("Saved. Next BUY/SELL/SL/TP will use your sound.");
      setTimeout(() => setCustomSoundMsg(""), 3500);
    };
    r.readAsDataURL(file);
  }
  function clearCustomSound() {
    try { localStorage.removeItem("ict.customExecSound"); } catch {}
    setCustomSoundUrl("");
    setCustomSoundMsg("Reverted to default ding.");
    setTimeout(() => setCustomSoundMsg(""), 2500);
  }
  function previewCustomSound() {
    try { new Audio(customSoundUrl).play(); } catch {}
  }

  // Persist on change
  useEffect(() => { try { localStorage.setItem("ict.visual", JSON.stringify(visualToggles)); } catch {} }, [visualToggles]);
  useEffect(() => { try { localStorage.setItem("ict.sounds", JSON.stringify(soundToggles)); } catch {} }, [soundToggles]);
  useEffect(() => { try { localStorage.setItem("ict.sounds.tf", JSON.stringify(soundTfFilter)); } catch {} }, [soundTfFilter]);
  useEffect(() => { try { localStorage.setItem("ict.autoReview", autoReview ? "1" : "0"); } catch {} }, [autoReview]);

  function toggleVisual(k) { setVisualToggles(v => ({ ...v, [k]: !v[k] })); }
  function toggleSound(k)  { setSoundToggles(v => ({ ...v, [k]: !(v[k] !== false) })); }
  function toggleTfSound(tf, k) {
    setSoundTfFilter(prev => {
      const cur = prev[tf] || {};
      // Currently muted? (explicit false). Toggle to "inherit" (delete key); otherwise mute.
      const isMuted = cur[k] === false;
      const next = { ...prev, [tf]: { ...cur } };
      if (isMuted) {
        // Was muted → restore to default (inherit master). Delete the key.
        delete next[tf][k];
        if (Object.keys(next[tf]).length === 0) delete next[tf];
      } else {
        // Was on (or undefined → inherit ON) → mute.
        next[tf][k] = false;
      }
      return next;
    });
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-tab">
      {/* AI Auto Review */}
      <div>
        <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-2">
          <i className="fa-solid fa-brain mr-1.5 text-purple-400"/> AI Auto Review
        </div>
        <label className="flex items-center justify-between bg-slate-900/60 border border-slate-800 rounded-lg px-4 py-3 cursor-pointer">
          <div>
            <div className="text-sm text-white font-bold">Pop the AI review modal after every trade</div>
            <div className="text-[10px] text-slate-500">Off = trade still gets analyzed silently; you can open it from history.</div>
          </div>
          <input data-testid="toggle-auto-review" type="checkbox" checked={autoReview} onChange={() => setAutoReview(v => !v)}
            className="w-5 h-5 accent-amber-400 cursor-pointer"/>
        </label>
      </div>

      {/* V2.6.8 — Custom Execution Sound (only when SKU owned) */}
      {customSoundOwned && (
        <div data-testid="custom-exec-sound-panel">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-2">
            <i className="fa-solid fa-volume-high mr-1.5 text-emerald-300"/> Custom Execution Sound
          </div>
          <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3 space-y-2">
            <div className="text-[11px] text-slate-400">
              Plays on every BUY/SELL fill AND on every SL / TP / liquidation close. Max 800 KB MP3/WAV/OGG.
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input data-testid="custom-exec-upload" type="file" accept="audio/*" onChange={onCustomSoundFile}
                className="text-xs text-slate-400 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-emerald-500 file:text-slate-900 file:font-bold cursor-pointer"/>
              {customSoundUrl && (
                <>
                  <button data-testid="custom-exec-preview" onClick={previewCustomSound} className="px-2 py-1 text-[10px] font-bold bg-slate-800 hover:bg-slate-700 rounded">
                    <i className="fa-solid fa-play mr-1"/> Preview
                  </button>
                  <button data-testid="custom-exec-clear" onClick={clearCustomSound} className="px-2 py-1 text-[10px] font-bold bg-rose-600/30 hover:bg-rose-600/50 border border-rose-500/50 text-rose-200 rounded">
                    Revert to default
                  </button>
                </>
              )}
            </div>
            <label className="flex items-center justify-between pt-1 cursor-pointer">
              <div>
                <div className="text-xs text-white font-bold">Use my uploaded sound</div>
                <div className="text-[9px] text-slate-500">Off = falls back to the default ding even though a file is loaded.</div>
              </div>
              <input data-testid="custom-exec-enabled" type="checkbox" checked={customSoundEnabled}
                onChange={() => setCustomSoundEnabled(v => !v)}
                className="w-5 h-5 accent-emerald-400 cursor-pointer"/>
            </label>
            {customSoundMsg && <div className="text-[11px] text-emerald-300">{customSoundMsg}</div>}
          </div>
        </div>
      )}

      {/* Chart Overlays */}
      <div>
        <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-2">
          <i className="fa-solid fa-eye mr-1.5 text-blue-400"/> ICT Chart Overlays
        </div>
        <div className="grid grid-cols-2 gap-2">
          {ICT_TOGGLES.map(t => (
            <label key={t.key} className="flex items-center justify-between bg-slate-900/60 border border-slate-800 rounded-lg px-3 py-2 cursor-pointer">
              <span className="text-xs text-slate-300">{t.label}</span>
              <input data-testid={`toggle-visual-${t.key}`} type="checkbox" checked={!!visualToggles[t.key]}
                onChange={() => toggleVisual(t.key)} className="accent-blue-400 cursor-pointer"/>
            </label>
          ))}
        </div>
      </div>

      {/* Master per-event audio */}
      <div>
        <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-2">
          <i className="fa-solid fa-bell mr-1.5 text-amber-400"/> Audio Alerts
        </div>
        <div className="grid grid-cols-2 gap-2">
          {SOUND_TOGGLES.map(s => (
            <label key={s.key} className="flex items-center justify-between bg-slate-900/60 border border-slate-800 rounded-lg px-3 py-2 cursor-pointer">
              <span className="text-xs text-slate-300">{s.label}</span>
              <input data-testid={`toggle-sound-${s.key}`} type="checkbox" checked={soundToggles[s.key] !== false}
                onChange={() => toggleSound(s.key)} className="accent-amber-400 cursor-pointer"/>
            </label>
          ))}
        </div>
      </div>

      {/* Per-timeframe × per-concept matrix */}
      <div>
        <div className="flex items-baseline gap-2 mb-2">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">
            <i className="fa-solid fa-filter mr-1.5 text-purple-400"/> Per-Timeframe Filter
          </div>
          <div className="text-[10px] text-slate-600 italic">Tap a cell to silence that concept on that timeframe only.</div>
        </div>
        <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-2 overflow-x-auto" data-testid="sound-tf-matrix">
          <table className="w-full text-[10px]">
            <thead>
              <tr className="text-slate-500">
                <th className="text-left px-1.5 py-1 font-bold">TF \ Concept</th>
                {SOUND_TOGGLES.map(s => (
                  <th key={s.key} className="text-center px-1 py-1 font-bold uppercase tracking-tight" title={s.label}>
                    {s.key.replace("SWEEP_", "S.")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TIMEFRAMES.map(tf => (
                <tr key={tf} className="border-t border-slate-800/60">
                  <td className="px-1.5 py-1 text-purple-300 font-bold">{tf}</td>
                  {SOUND_TOGGLES.map(s => {
                    const on = soundTfFilter[tf]?.[s.key] !== false;
                    return (
                      <td key={s.key} className="text-center px-1 py-1">
                        <button data-testid={`tf-sound-${tf}-${s.key}`} onClick={() => toggleTfSound(tf, s.key)}
                          title={`${tf} · ${s.label}${on ? "" : " (muted)"}`}
                          className={`w-5 h-5 rounded-full transition-colors ${on ? "bg-emerald-500/80 hover:bg-emerald-500" : "bg-slate-700 hover:bg-slate-600"}`}>
                          <i className={`fa-solid ${on ? "fa-volume-high" : "fa-volume-xmark"} text-[8px] text-white`}/>
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-[10px] text-slate-600 italic">
        Changes sync to the Sandbox in real time via localStorage. No reload required.
      </div>
    </div>
  );
}
